import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Settings, Loader2, Users, ShieldCheck, User,
  DollarSign, Megaphone, Palette, Wrench, History,
} from 'lucide-react';
import logger from '../utils/logger';
import BrandingTab from '../components/BrandingTab';
import DuplicateShiftsCleanup from '../components/DuplicateShiftsCleanup';
import CeoLifeAccessManager from '../components/CeoLifeAccessManager';
import DonorCrmAccessManager from '../components/DonorCrmAccessManager';
import MarketingAccessManager from '../components/MarketingAccessManager';
import HrAccessManager from '../components/HrAccessManager';
import StaffAccessTab from './settings/StaffAccessTab';
import AuditLogTab from './settings/AuditLogTab';
import ProfileTab from './settings/ProfileTab';
import TeamTab from './settings/TeamTab';
import SecurityTab from './settings/SecurityTab';
import ServicesTab from './settings/ServicesTab';
import PrivatePayProgramsTab from './settings/PrivatePayProgramsTab';
import BillingTab from './settings/BillingTab';
import CommsTab from './settings/CommsTab';
import { AnnouncementDialog, AccountDialog, ResetPasswordDialog } from './settings/SettingsDialogs';

export default function SettingsPage() {
  const { api, user: currentUser, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Profile + password
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Account dialog
  const [accountOpen, setAccountOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({ name: '', email: '', password: '', phone: '', role: 'employee', service_lines: [], max_weekly_hours: 40 });
  const [accountSaving, setAccountSaving] = useState(false);

  // Reset password dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Billing
  const [billingRates, setBillingRates] = useState({ day_program: 6.32, employment: 20.60, in_home: 11.10, private_pay: 0 });
  const [billingSaving, setBillingSaving] = useState(false);
  const [exportForm, setExportForm] = useState(() => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start_date: monthStart.toISOString().split('T')[0], end_date: today.toISOString().split('T')[0], service_line: '' };
  });
  const [exporting, setExporting] = useState(false);

  // Announcements + closures
  const [announcements, setAnnouncements] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', body: '', priority: 'normal', target: 'all', expires_at: '' });
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [closures, setClosures] = useState([]);
  const [closureForm, setClosureForm] = useState({ date: '', reason: '' });
  const [closureSaving, setClosureSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, usersRes, ratesRes, announcementsRes, closuresRes] = await Promise.all([
        api.get('/settings'),
        api.get('/employees/all'),
        api.get('/settings/billing-rates'),
        api.get('/announcements'),
        api.get('/closures'),
      ]);
      setSettings(settingsRes.data);
      setAllUsers(usersRes.data);
      if (ratesRes.data?.rates) setBillingRates(ratesRes.data.rates);
      setAnnouncements(announcementsRes.data);
      setClosures(closuresRes.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (currentUser) {
      setProfileForm({ name: currentUser.name || '', email: currentUser.email || '', phone: currentUser.phone || '' });
    }
  }, [currentUser]);

  const updateService = (key, field, value) => {
    setSettings(prev => ({ ...prev, services: { ...prev.services, [key]: { ...prev.services[key], [field]: value } } }));
    setSaved(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      setSaved(true);
      toast.success('Settings saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put('/auth/profile', profileForm);
      await checkAuth();
      toast.success('Profile updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update profile'); }
    setProfileSaving(false);
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.newPw });
      setPwForm({ current: '', newPw: '', confirm: '' });
      toast.success('Password changed successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    setPwSaving(false);
  };

  const openCreateAccount = () => {
    setEditingAccount(null);
    setAccountForm({ name: '', email: '', password: '', phone: '', role: 'employee', service_lines: [], max_weekly_hours: 40 });
    setAccountOpen(true);
  };

  const openEditAccount = (u) => {
    setEditingAccount(u);
    setAccountForm({ name: u.name, email: u.email, password: '', phone: u.phone || '', role: u.role || 'employee', service_lines: u.service_lines || [], max_weekly_hours: u.max_weekly_hours || 40 });
    setAccountOpen(true);
  };

  const handleSaveAccount = async () => {
    setAccountSaving(true);
    try {
      if (editingAccount) {
        await api.put(`/employees/${editingAccount.id}`, { name: accountForm.name, phone: accountForm.phone, service_lines: accountForm.service_lines, max_weekly_hours: accountForm.max_weekly_hours });
        if (accountForm.role !== editingAccount.role) {
          await api.put(`/employees/${editingAccount.id}/role?role=${accountForm.role}`);
        }
      } else {
        await api.post('/employees', { ...accountForm });
        if (accountForm.role !== 'employee') {
          const empRes = await api.get('/employees');
          const newEmp = empRes.data.find(e => e.email === accountForm.email.toLowerCase());
          if (newEmp) await api.put(`/employees/${newEmp.id}/role?role=${accountForm.role}`);
        }
      }
      setAccountOpen(false);
      toast.success(editingAccount ? 'Account updated' : 'Account created');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving account'); }
    setAccountSaving(false);
  };

  const handleDeleteAccount = async (id, name) => {
    if (!window.confirm(`Delete ${name}'s account? This cannot be undone.`)) return;
    try { await api.delete(`/employees/${id}`); toast.success('Account deleted'); fetchData(); }
    catch (e) { toast.error('Failed to delete account'); }
  };

  const handleToggleActive = async (id, currentActive, name) => {
    try { await api.put(`/employees/${id}/toggle-active`); toast.success(`${name} ${currentActive ? 'deactivated' : 'activated'}`); fetchData(); }
    catch (e) { toast.error('Failed to toggle account'); }
  };

  const openResetPassword = (u) => {
    setResetTarget(u); setResetPw(''); setResetOpen(true);
  };

  const handleResetPassword = async () => {
    if (resetPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetSaving(true);
    try {
      await api.post(`/employees/${resetTarget.id}/reset-password`, { new_password: resetPw });
      setResetOpen(false);
      toast.success(`Password reset for ${resetTarget.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
    setResetSaving(false);
  };

  const handleRoleChange = async (userId, newRole) => {
    try { await api.put(`/employees/${userId}/role?role=${newRole}`); toast.success('Role updated'); fetchData(); }
    catch (e) { logger.error(e); }
  };

  const toggleFormService = (sl) => {
    setAccountForm(p => ({ ...p, service_lines: p.service_lines.includes(sl) ? p.service_lines.filter(s => s !== sl) : [...p.service_lines, sl] }));
  };

  const handleSaveBillingRates = async () => {
    setBillingSaving(true);
    try { await api.put('/settings/billing-rates', { rates: billingRates }); toast.success('Billing rates saved'); }
    catch (e) { toast.error('Failed to save rates'); }
    setBillingSaving(false);
  };

  const handleExportBilling = async () => {
    if (!exportForm.start_date || !exportForm.end_date) { toast.error('Pick a date range'); return; }
    setExporting(true);
    try {
      const params = new URLSearchParams({ start_date: exportForm.start_date, end_date: exportForm.end_date });
      if (exportForm.service_line) params.append('service_line', exportForm.service_line);
      const res = await api.get(`/dashboard/billing/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `billing-${exportForm.start_date}-to-${exportForm.end_date}.csv`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Billing report downloaded');
    } catch (e) { toast.error('Export failed'); logger.error(e); }
    setExporting(false);
  };

  const openCreateAnnouncement = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({ title: '', body: '', priority: 'normal', target: 'all', expires_at: '' });
    setAnnouncementOpen(true);
  };
  const openEditAnnouncement = (a) => {
    setEditingAnnouncement(a);
    setAnnouncementForm({ title: a.title, body: a.body || '', priority: a.priority || 'normal', target: a.target || 'all', expires_at: a.expires_at || '' });
    setAnnouncementOpen(true);
  };
  const handleSaveAnnouncement = async () => {
    setAnnouncementSaving(true);
    try {
      if (editingAnnouncement) await api.put(`/announcements/${editingAnnouncement.id}`, announcementForm);
      else await api.post('/announcements', announcementForm);
      setAnnouncementOpen(false);
      toast.success(editingAnnouncement ? 'Announcement updated' : 'Announcement posted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setAnnouncementSaving(false);
  };
  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await api.delete(`/announcements/${id}`); toast.success('Deleted'); fetchData(); } catch (e) { toast.error('Error'); }
  };
  const handleToggleAnnouncement = async (a) => {
    try { await api.put(`/announcements/${a.id}`, { is_active: !a.is_active }); fetchData(); } catch (e) { toast.error('Error'); }
  };

  const handleAddClosure = async () => {
    if (!closureForm.date) { toast.error('Date required'); return; }
    setClosureSaving(true);
    try {
      await api.post('/closures', closureForm);
      setClosureForm({ date: '', reason: '' });
      toast.success('Closure added');
      fetchData();
    } catch (e) { toast.error('Error'); }
    setClosureSaving(false);
  };
  const handleDeleteClosure = async (id) => {
    try { await api.delete(`/closures/${id}`); toast.success('Removed'); fetchData(); } catch (e) { toast.error('Error'); }
  };

  if (loading || !settings) {
    return <div className="p-8 text-center text-[#8E85A0]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }

  const pwStrength = pwForm.newPw.length === 0 ? null :
    pwForm.newPw.length < 8 ? 'weak' :
    (pwForm.newPw.length >= 12 && /[A-Z]/.test(pwForm.newPw) && /[0-9]/.test(pwForm.newPw) && /[^A-Za-z0-9]/.test(pwForm.newPw)) ? 'strong' : 'good';

  const isAdmin = currentUser?.role === 'administrator';

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-6xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h1>
        <p className="text-sm text-[#8E85A0] mt-1">Manage your profile, team accounts, and service configurations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F0EDF6] border border-[#DED8EB] h-10" data-testid="settings-tabs">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-profile">
            <User className="h-3.5 w-3.5" /> My Profile
          </TabsTrigger>
          <TabsTrigger value="accounts" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-accounts">
            <Users className="h-3.5 w-3.5" /> Team
            <Badge className="text-[10px] bg-[#DED8EB] text-[#5A4D6E] h-5 min-w-5 rounded-full">{allUsers.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-security">
            <ShieldCheck className="h-3.5 w-3.5" /> Security
          </TabsTrigger>
          <TabsTrigger value="service" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-service">
            <Settings className="h-3.5 w-3.5" /> Services
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-billing">
            <DollarSign className="h-3.5 w-3.5" /> Billing
          </TabsTrigger>
          <TabsTrigger value="comms" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-comms">
            <Megaphone className="h-3.5 w-3.5" /> Comms
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-branding">
            <Palette className="h-3.5 w-3.5" /> Branding
          </TabsTrigger>
          <TabsTrigger value="tools" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-tools">
            <Wrench className="h-3.5 w-3.5" /> Tools
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="staff_access" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-staff-access">
              <ShieldCheck className="h-3.5 w-3.5" /> Staff Access
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="audit_log" className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm" data-testid="settings-tab-audit">
              <History className="h-3.5 w-3.5" /> Audit Log
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-5 space-y-5">
          <ProfileTab
            currentUser={currentUser}
            profileForm={profileForm} setProfileForm={setProfileForm}
            profileSaving={profileSaving} handleSaveProfile={handleSaveProfile}
            pwForm={pwForm} setPwForm={setPwForm}
            pwSaving={pwSaving} handleChangePassword={handleChangePassword}
            showCurrent={showCurrent} setShowCurrent={setShowCurrent}
            showNew={showNew} setShowNew={setShowNew}
            pwStrength={pwStrength}
          />
        </TabsContent>

        <TabsContent value="accounts" className="mt-5 space-y-4">
          <TeamTab
            allUsers={allUsers} currentUser={currentUser}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            openCreateAccount={openCreateAccount}
            openEditAccount={openEditAccount}
            openResetPassword={openResetPassword}
            handleToggleActive={handleToggleActive}
            handleDeleteAccount={handleDeleteAccount}
            handleRoleChange={handleRoleChange}
          />
        </TabsContent>

        <TabsContent value="security" className="mt-5 space-y-5">
          <SecurityTab />
        </TabsContent>

        <TabsContent value="service" className="mt-5 space-y-4">
          <ServicesTab settings={settings} setSettings={setSettings} updateService={updateService} saving={saving} saved={saved} handleSaveSettings={handleSaveSettings} />
          <PrivatePayProgramsTab />
        </TabsContent>

        <TabsContent value="billing" className="mt-5 space-y-5">
          <BillingTab
            billingRates={billingRates} setBillingRates={setBillingRates}
            billingSaving={billingSaving} handleSaveBillingRates={handleSaveBillingRates}
            exportForm={exportForm} setExportForm={setExportForm}
            exporting={exporting} handleExportBilling={handleExportBilling}
          />
        </TabsContent>

        <TabsContent value="comms" className="mt-5 space-y-5">
          <CommsTab
            announcements={announcements}
            openCreateAnnouncement={openCreateAnnouncement}
            openEditAnnouncement={openEditAnnouncement}
            handleToggleAnnouncement={handleToggleAnnouncement}
            handleDeleteAnnouncement={handleDeleteAnnouncement}
            closures={closures} closureForm={closureForm} setClosureForm={setClosureForm}
            closureSaving={closureSaving}
            handleAddClosure={handleAddClosure}
            handleDeleteClosure={handleDeleteClosure}
          />
        </TabsContent>

        <TabsContent value="branding" className="mt-5 space-y-5">
          <BrandingTab api={api} />
        </TabsContent>

        <TabsContent value="tools" className="mt-5 space-y-5">
          <CeoLifeAccessManager api={api} />
          <DonorCrmAccessManager api={api} />
          <MarketingAccessManager api={api} />
          <HrAccessManager api={api} />
          <DuplicateShiftsCleanup api={api} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="staff_access" className="mt-5 space-y-5">
            <StaffAccessTab api={api} />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="audit_log" className="mt-5 space-y-5">
            <AuditLogTab api={api} />
          </TabsContent>
        )}
      </Tabs>

      <AnnouncementDialog
        open={announcementOpen} onOpenChange={setAnnouncementOpen}
        editingAnnouncement={editingAnnouncement}
        form={announcementForm} setForm={setAnnouncementForm}
        saving={announcementSaving} onSave={handleSaveAnnouncement}
      />
      <AccountDialog
        open={accountOpen} onOpenChange={setAccountOpen}
        editingAccount={editingAccount}
        form={accountForm} setForm={setAccountForm}
        saving={accountSaving} onSave={handleSaveAccount}
        toggleFormService={toggleFormService}
      />
      <ResetPasswordDialog
        open={resetOpen} onOpenChange={setResetOpen}
        target={resetTarget} password={resetPw} setPassword={setResetPw}
        saving={resetSaving} onSave={handleResetPassword}
      />
    </div>
  );
}
